import { Injectable } from '@angular/core';
import { BuilderComponentType } from '../interfaces/builder-screen.models';
import { 
  InspectorProperty, 
  PropertyGroup, 
  ComponentPreset, 
  PropertyHelp 
} from '../interfaces/inspector-config.models';

@Injectable({
  providedIn: 'root'
})
export class InspectorConfigService {

  // Mapeamento de propriedades sensível ao contexto
  private readonly PROPERTY_MAP: InspectorProperty[] = [
    // Propriedades básicas (todos os componentes)
    {
      key: 'label',
      labelKey: 'builderScreen.inspector.label',
      helpTextKey: 'builderScreen.inspector.label.help',
      type: 'text',
      component: ['tabs', 'text', 'textarea', 'number', 'switch', 'simpletable', 'table', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search', 'divider', 'section'],
      advanced: false,
      group: 'appearance'
    },
    {
      key: 'placeholder',
      labelKey: 'builderScreen.inspector.placeholder',
      helpTextKey: 'builderScreen.inspector.placeholder.help',
      type: 'text',
      component: ['text', 'textarea', 'number', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'search'],
      advanced: false,
      group: 'appearance'
    },
    {
      key: 'required',
      labelKey: 'builderScreen.inspector.required',
      helpTextKey: 'builderScreen.inspector.required.help',
      type: 'checkbox',
      component: ['text', 'textarea', 'number', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search'],
      advanced: false,
      group: 'validation'
    },
    {
      key: 'isDisabled',
      labelKey: 'builderScreen.inspector.isDisabled',
      helpTextKey: 'builderScreen.inspector.isDisabled.help',
      type: 'checkbox',
      component: ['text', 'textarea', 'number', 'switch', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search'],
      advanced: false,
      group: 'validation'
    },
    {
      key: 'dataPath',
      labelKey: 'builderScreen.inspector.dataPath',
      helpTextKey: 'builderScreen.inspector.dataPath.help',
      type: 'text',
      component: ['text', 'textarea', 'number', 'switch', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search', 'image'],
      advanced: false,
      group: 'data'
    },
    {
      key: 'colSpan',
      labelKey: 'builderScreen.inspector.colspan',
      helpTextKey: 'builderScreen.inspector.colspan.help',
      type: 'number',
      component: ['tabs', 'text', 'textarea', 'number', 'switch', 'simpletable', 'table', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search', 'divider', 'section'],
      advanced: false,
      group: 'layout',
      min: 1,
      max: 24
    },
    {
      key: 'rowSpan',
      labelKey: 'builderScreen.inspector.rowspan',
      helpTextKey: 'builderScreen.inspector.rowspan.help',
      type: 'number',
      component: ['tabs', 'text', 'textarea', 'number', 'switch', 'simpletable', 'table', 'date', 'select', 'dropdown', 'querySelect', 'keyValue', 'keyValueObject', 'tag', 'search', 'divider', 'section'],
      advanced: false,
      group: 'layout',
      min: 1,
      max: 50
    },

    // Propriedades específicas de Text/TextArea
    {
      key: 'defaultValue',
      labelKey: 'builderScreen.inspector.defaultValue',
      helpTextKey: 'builderScreen.inspector.defaultValue.help',
      type: 'text',
      component: ['text', 'textarea'],
      advanced: false,
      group: 'behavior'
    },
    {
      key: 'minLength',
      labelKey: 'builderScreen.inspector.minLength',
      helpTextKey: 'builderScreen.inspector.minLength.help',
      type: 'number',
      component: ['text', 'textarea'],
      advanced: true,
      group: 'validation',
      min: 0
    },
    {
      key: 'maxLength',
      labelKey: 'builderScreen.inspector.maxLength',
      helpTextKey: 'builderScreen.inspector.maxLength.help',
      type: 'number',
      component: ['text', 'textarea'],
      advanced: true,
      group: 'validation',
      min: 1
    },
    {
      key: 'minRows',
      labelKey: 'builderScreen.inspector.minRows',
      helpTextKey: 'builderScreen.inspector.minRows.help',
      type: 'number',
      component: ['textarea'],
      advanced: true,
      group: 'appearance',
      min: 1,
      max: 20
    },

    // Propriedades específicas de Number
    {
      key: 'min',
      labelKey: 'builderScreen.inspector.min',
      helpTextKey: 'builderScreen.inspector.min.help',
      type: 'number',
      component: ['number'],
      advanced: true,
      group: 'validation'
    },
    {
      key: 'max',
      labelKey: 'builderScreen.inspector.max',
      helpTextKey: 'builderScreen.inspector.max.help',
      type: 'number',
      component: ['number'],
      advanced: true,
      group: 'validation'
    },
    {
      key: 'step',
      labelKey: 'builderScreen.inspector.step',
      helpTextKey: 'builderScreen.inspector.step.help',
      type: 'number',
      component: ['number'],
      advanced: true,
      group: 'behavior',
      min: 0.01,
      step: 0.01
    },
    {
      key: 'precision',
      labelKey: 'builderScreen.inspector.precision',
      helpTextKey: 'builderScreen.inspector.precision.help',
      type: 'number',
      component: ['number'],
      advanced: true,
      group: 'behavior',
      min: 0,
      max: 10
    },

    // Propriedades de formatação de Number (NG-ZORRO)
    {
      key: 'decimalPlaces',
      labelKey: 'builderScreen.inspector.decimalPlaces',
      helpTextKey: 'builderScreen.inspector.decimalPlaces.help',
      type: 'number',
      component: ['number'],
      advanced: true,
      group: 'formatting',
      min: 0,
      max: 10
    },
    {
      key: 'formatThousands',
      labelKey: 'builderScreen.inspector.formatThousands',
      helpTextKey: 'builderScreen.inspector.formatThousands.help',
      type: 'checkbox',
      component: ['number'],
      advanced: true,
      group: 'formatting'
    },
    {
      key: 'thousandSeparator',
      labelKey: 'builderScreen.inspector.thousandSeparator',
      helpTextKey: 'builderScreen.inspector.thousandSeparator.help',
      type: 'select',
      options: [{ value: '.', label: 'Ponto (.)' }, { value: ',', label: 'Vírgula (,)' }],
      component: ['number'],
      advanced: true,
      group: 'formatting'
    },
    {
      key: 'decimalSeparator',
      labelKey: 'builderScreen.inspector.decimalSeparator',
      helpTextKey: 'builderScreen.inspector.decimalSeparator.help',
      type: 'select',
      options: [{ value: ',', label: 'Vírgula (,)' }, { value: '.', label: 'Ponto (.)' }],
      component: ['number'],
      advanced: true,
      group: 'formatting'
    },
    {
      key: 'addonBefore',
      labelKey: 'builderScreen.inspector.addonBefore',
      helpTextKey: 'builderScreen.inspector.addonBefore.help',
      type: 'text',
      component: ['number'],
      advanced: true,
      group: 'formatting'
    },
    {
      key: 'addonAfter',
      labelKey: 'builderScreen.inspector.addonAfter',
      helpTextKey: 'builderScreen.inspector.addonAfter.help',
      type: 'text',
      component: ['number'],
      advanced: true,
      group: 'formatting'
    },

    // Propriedades específicas de Switch
    {
      key: 'defaultValue',
      labelKey: 'builderScreen.inspector.defaultValue',
      helpTextKey: 'builderScreen.inspector.defaultValue.help',
      type: 'checkbox',
      component: ['switch'],
      advanced: false,
      group: 'behavior'
    },
    {
      key: 'checkedChildren',
      labelKey: 'builderScreen.inspector.checkedChildren',
      helpTextKey: 'builderScreen.inspector.checkedChildren.help',
      type: 'text',
      component: ['switch'],
      advanced: true,
      group: 'appearance'
    },
    {
      key: 'unCheckedChildren',
      labelKey: 'builderScreen.inspector.unCheckedChildren',
      helpTextKey: 'builderScreen.inspector.unCheckedChildren.help',
      type: 'text',
      component: ['switch'],
      advanced: true,
      group: 'appearance'
    },

    // Propriedades específicas de Table
    {
      key: 'sorting',
      labelKey: 'builderScreen.inspector.sorting',
      helpTextKey: 'builderScreen.inspector.sorting.help',
      type: 'checkbox',
      component: ['table'],
      advanced: true,
      group: 'behavior'
    },
    {
      key: 'filtering',
      labelKey: 'builderScreen.inspector.filtering',
      helpTextKey: 'builderScreen.inspector.filtering.help',
      type: 'checkbox',
      component: ['table'],
      advanced: true,
      group: 'behavior'
    },
    {
      key: 'pagination',
      labelKey: 'builderScreen.inspector.pagination',
      helpTextKey: 'builderScreen.inspector.pagination.help',
      type: 'checkbox',
      component: ['table'],
      advanced: true,
      group: 'behavior'
    },

    // Propriedades de componentes Webhook V5
    {
      key: 'dateFormat',
      labelKey: 'builderScreen.inspector.dateFormat',
      helpTextKey: 'builderScreen.inspector.dateFormat.help',
      type: 'select',
      component: ['date'],
      advanced: false,
      group: 'data',
      options: [
        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
        { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' }
      ]
    },
    {
      key: 'showTime',
      labelKey: 'builderScreen.inspector.showTime',
      helpTextKey: 'builderScreen.inspector.showTime.help',
      type: 'checkbox',
      component: ['date'],
      advanced: false,
      group: 'behavior'
    },
    {
      key: 'multiple',
      labelKey: 'builderScreen.inspector.multiple',
      helpTextKey: 'builderScreen.inspector.multiple.help',
      type: 'checkbox',
      component: ['select', 'dropdown'],
      advanced: false,
      group: 'behavior'
    },
    {
      key: 'showSearch',
      labelKey: 'builderScreen.inspector.showSearch',
      helpTextKey: 'builderScreen.inspector.showSearch.help',
      type: 'checkbox',
      component: ['select', 'dropdown'],
      advanced: true,
      group: 'behavior'
    },
    {
      key: 'condition',
      labelKey: 'builderScreen.inspector.condition',
      helpTextKey: 'builderScreen.inspector.condition.help',
      type: 'textarea',
      component: ['querySelect'],
      advanced: true,
      group: 'data'
    },
    {
      key: 'showKey',
      labelKey: 'builderScreen.inspector.showKey',
      helpTextKey: 'builderScreen.inspector.showKey.help',
      type: 'checkbox',
      component: ['querySelect', 'search'],
      advanced: true,
      group: 'behavior'
    },
    {
      key: 'entityName',
      labelKey: 'builderScreen.inspector.entityName',
      helpTextKey: 'builderScreen.inspector.entityName.help',
      type: 'text',
      component: ['querySelect', 'search'],
      advanced: true,
      group: 'data'
    },
    {
      key: 'keyField',
      labelKey: 'builderScreen.inspector.keyField',
      helpTextKey: 'builderScreen.inspector.keyField.help',
      type: 'text',
      component: ['search'],
      advanced: true,
      group: 'data'
    },
    {
      key: 'valueField',
      labelKey: 'builderScreen.inspector.valueField',
      helpTextKey: 'builderScreen.inspector.valueField.help',
      type: 'text',
      component: ['search'],
      advanced: true,
      group: 'data'
    },
    {
      key: 'dashed',
      labelKey: 'builderScreen.inspector.dashed',
      helpTextKey: 'builderScreen.inspector.dashed.help',
      type: 'checkbox',
      component: ['divider'],
      advanced: false,
      group: 'appearance'
    },
    {
      key: 'type',
      labelKey: 'builderScreen.inspector.dividerType',
      helpTextKey: 'builderScreen.inspector.dividerType.help',
      type: 'select',
      component: ['divider'],
      advanced: false,
      group: 'appearance',
      options: [
        { value: 'horizontal', label: 'Horizontal' },
        { value: 'vertical', label: 'Vertical' }
      ]
    },
    {
      key: 'orientation',
      labelKey: 'builderScreen.inspector.orientation',
      helpTextKey: 'builderScreen.inspector.orientation.help',
      type: 'select',
      component: ['divider'],
      advanced: true,
      group: 'appearance',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' }
      ]
    },
    {
      key: 'color',
      labelKey: 'builderScreen.inspector.color',
      helpTextKey: 'builderScreen.inspector.color.help',
      type: 'color',
      component: ['divider'],
      advanced: true,
      group: 'appearance'
    },
    {
      key: 'title',
      labelKey: 'builderScreen.inspector.sectionTitle',
      helpTextKey: 'builderScreen.inspector.sectionTitle.help',
      type: 'text',
      component: ['section'],
      advanced: false,
      group: 'appearance'
    },
    {
      key: 'collapsible',
      labelKey: 'builderScreen.inspector.collapsible',
      helpTextKey: 'builderScreen.inspector.collapsible.help',
      type: 'checkbox',
      component: ['section'],
      advanced: false,
      group: 'behavior'
    },
    {
      key: 'collapsed',
      labelKey: 'builderScreen.inspector.collapsed',
      helpTextKey: 'builderScreen.inspector.collapsed.help',
      type: 'checkbox',
      component: ['section'],
      advanced: true,
      group: 'behavior'
    },
    
    // Propriedades específicas de Image
    {
      key: 'imageUrl',
      labelKey: 'builderScreen.inspector.imageUrl',
      helpTextKey: 'builderScreen.inspector.imageUrl.help',
      type: 'textarea',
      component: ['image'],
      advanced: false,
      group: 'content'
    },
    {
      key: 'alt',
      labelKey: 'builderScreen.inspector.alt',
      helpTextKey: 'builderScreen.inspector.alt.help',
      type: 'text',
      component: ['image'],
      advanced: false,
      group: 'appearance'
    },
    {
      key: 'width',
      labelKey: 'builderScreen.inspector.width',
      type: 'number',
      component: ['image'],
      advanced: false,
      group: 'appearance',
      min: 0
    },
    {
      key: 'height',
      labelKey: 'builderScreen.inspector.height',
      type: 'number',
      component: ['image'],
      advanced: false,
      group: 'appearance',
      min: 0
    },
    {
      key: 'borderRadius',
      labelKey: 'builderScreen.inspector.borderRadius',
      type: 'number',
      component: ['image'],
      advanced: false,
      group: 'appearance',
      min: 0
    },
    {
      key: 'enableLightbox',
      labelKey: 'builderScreen.inspector.enableLightbox',
      helpTextKey: 'builderScreen.inspector.enableLightbox.help',
      type: 'checkbox',
      component: ['image'],
      advanced: false,
      group: 'behavior'
    }
  ];

  // Grupos de propriedades para organização lógica
  private readonly PROPERTY_GROUPS: PropertyGroup[] = [
    {
      id: 'validation',
      titleKey: 'builderScreen.groups.validation',
      icon: 'safety-certificate',
      description: 'Configure field validation rules',
      properties: ['required', 'minLength', 'maxLength', 'min', 'max']
    },
    {
      id: 'appearance',
      titleKey: 'builderScreen.groups.appearance',
      icon: 'bg-colors',
      description: 'Control how the component looks',
      properties: ['label', 'placeholder', 'checkedChildren', 'unCheckedChildren', 'minRows', 'dashed', 'type', 'orientation', 'color', 'title']
    },
    {
      id: 'behavior',
      titleKey: 'builderScreen.groups.behavior',
      icon: 'interaction',
      description: 'Define component behavior',
      properties: ['defaultValue', 'multiple', 'showSearch', 'showKey', 'step', 'precision', 'sorting', 'filtering', 'pagination', 'showTime', 'collapsible', 'collapsed']
    },
    {
      id: 'data',
      titleKey: 'builderScreen.groups.data',
      icon: 'database',
      description: 'Configure data source and format',
      properties: ['condition', 'entityName', 'dateFormat', 'keyField', 'valueField']
    },
    {
      id: 'layout',
      titleKey: 'builderScreen.groups.layout',
      icon: 'layout',
      description: 'Control component size and position',
      properties: ['colSpan', 'rowSpan']
    },
    {
      id: 'formatting',
      titleKey: 'builderScreen.groups.formatting',
      icon: 'format-painter',
      description: 'Configure number display format',
      properties: ['decimalPlaces', 'formatThousands', 'thousandSeparator', 'decimalSeparator', 'addonBefore', 'addonAfter']
    }
  ];

  // Presets de componentes para casos de uso comuns
  private readonly COMPONENT_PRESETS: ComponentPreset[] = [
    {
      id: 'email-input',
      name: 'Campo de Email',
      description: 'Campo de texto otimizado para endereços de email',
      componentType: 'text',
      props: {
        label: 'Email',
        placeholder: 'Digite seu endereço de email',
        required: true,
        minLength: 5,
        maxLength: 254,
        colSpan: 12,
        rowSpan: 1
      }
    },
    {
      id: 'phone-input',
      name: 'Campo de Telefone',
      description: 'Campo de texto para números de telefone',
      componentType: 'text',
      props: {
        label: 'Telefone',
        placeholder: '(11) 99999-9999',
        required: false,
        minLength: 10,
        maxLength: 15,
        colSpan: 12,
        rowSpan: 1
      }
    },
    {
      id: 'password-input',
      name: 'Campo de Senha',
      description: 'Campo de texto para senhas com validação',
      componentType: 'text',
      props: {
        label: 'Senha',
        placeholder: 'Digite sua senha',
        required: true,
        minLength: 8,
        maxLength: 50,
        colSpan: 12,
        rowSpan: 1
      }
    },
    {
      id: 'birth-date',
      name: 'Data de Nascimento',
      description: 'Seletor de data para datas de nascimento',
      componentType: 'date',
      props: {
        label: 'Data de Nascimento',
        dateFormat: 'DD/MM/YYYY',
        showTime: false,
        required: true,
        colSpan: 12,
        rowSpan: 1
      }
    },
    {
      id: 'currency-input',
      name: 'Campo de Valor',
      description: 'Campo numérico para valores monetários',
      componentType: 'number',
      props: {
        label: 'Valor',
        placeholder: '0,00',
        min: 0,
        max: 999999.99,
        step: 0.01,
        precision: 2,
        required: false,
        colSpan: 12,
        rowSpan: 1
      }
    },
    {
      id: 'description-textarea',
      name: 'Campo de Descrição',
      description: 'Área de texto para descrições longas',
      componentType: 'textarea',
      props: {
        label: 'Descrição',
        placeholder: 'Digite uma descrição detalhada...',
        minRows: 4,
        maxLength: 500,
        required: false,
        colSpan: 24,
        rowSpan: 2
      }
    }
  ];

  // Informações de ajuda das propriedades
  private readonly PROPERTY_HELP: PropertyHelp[] = [
    {
      property: 'minLength',
      titleKey: 'help.minLength.title',
      descriptionKey: 'help.minLength.description',
      examples: [
        {
          scenario: 'Campo de senha',
          value: 8,
          result: 'Usuários devem digitar pelo menos 8 caracteres'
        },
        {
          scenario: 'Campo de nome',
          value: 2,
          result: 'Nomes devem ter pelo menos 2 caracteres'
        }
      ],
      commonMistakes: [
        'Definir minLength muito alto para campos de nome',
        'Não considerar nomes internacionais'
      ]
    },
    {
      property: 'maxLength',
      titleKey: 'help.maxLength.title',
      descriptionKey: 'help.maxLength.description',
      examples: [
        {
          scenario: 'Campo de email',
          value: 254,
          result: 'Limite padrão para endereços de email'
        },
        {
          scenario: 'Campo de comentário',
          value: 500,
          result: 'Limite razoável para comentários'
        }
      ],
      commonMistakes: [
        'Definir limite muito baixo para campos de texto livre',
        'Não considerar diferentes idiomas'
      ]
    }
  ];

  /**
   * Get properties that should be shown for a specific component type
   */
  getPropertiesForComponent(componentType: BuilderComponentType): InspectorProperty[] {
    return this.PROPERTY_MAP.filter(prop => 
      prop.component.includes(componentType)
    );
  }

  /**
   * Get essential (non-advanced) properties for a component type
   */
  getEssentialProperties(componentType: BuilderComponentType): InspectorProperty[] {
    return this.getPropertiesForComponent(componentType)
      .filter(prop => !prop.advanced);
  }

  /**
   * Get advanced properties for a component type
   */
  getAdvancedProperties(componentType: BuilderComponentType): InspectorProperty[] {
    return this.getPropertiesForComponent(componentType)
      .filter(prop => prop.advanced);
  }

  /**
   * Check if component type has advanced properties
   */
  hasAdvancedProperties(componentType: BuilderComponentType): boolean {
    return this.getAdvancedProperties(componentType).length > 0;
  }

  /**
   * Get properties grouped by category
   */
  getPropertiesByGroup(componentType: BuilderComponentType): Map<string, InspectorProperty[]> {
    const properties = this.getPropertiesForComponent(componentType);
    const grouped = new Map<string, InspectorProperty[]>();
    
    properties.forEach(prop => {
      const group = prop.group || 'other';
      if (!grouped.has(group)) {
        grouped.set(group, []);
      }
      grouped.get(group)!.push(prop);
    });
    
    return grouped;
  }

  /**
   * Get all property groups
   */
  getPropertyGroups(): PropertyGroup[] {
    return [...this.PROPERTY_GROUPS];
  }

  /**
   * Get property group by ID
   */
  getPropertyGroup(groupId: string): PropertyGroup | undefined {
    return this.PROPERTY_GROUPS.find(group => group.id === groupId);
  }

  /**
   * Get presets for a specific component type
   */
  getPresetsForComponent(componentType: BuilderComponentType): ComponentPreset[] {
    return this.COMPONENT_PRESETS.filter(preset => 
      preset.componentType === componentType
    );
  }

  /**
   * Get all component presets
   */
  getAllPresets(): ComponentPreset[] {
    return [...this.COMPONENT_PRESETS];
  }

  /**
   * Get preset by ID
   */
  getPreset(presetId: string): ComponentPreset | undefined {
    return this.COMPONENT_PRESETS.find(preset => preset.id === presetId);
  }

  /**
   * Get help information for a property
   */
  getPropertyHelp(propertyKey: string): PropertyHelp | undefined {
    return this.PROPERTY_HELP.find(help => help.property === propertyKey);
  }

  /**
   * Get advanced header text based on component type
   */
  getAdvancedHeaderText(componentType: BuilderComponentType): string {
    const advancedCount = this.getAdvancedProperties(componentType).length;
    return `builderScreen.inspector.advancedSettings.${componentType}`;
  }
}

